/**
 * Space routing bridge — DXF ENTITIES + LAYOUT + BLOCK paper-space records
 * <-> DrawingDocument `spaces` array.
 *
 * Extracted from dxfBridge.ts because the multi-layout routing algorithm
 * (OBJECTS LAYOUT ↔ TABLES BLOCK_RECORD ↔ BLOCKS paper-space blocks ↔
 * top-level ENTITIES section) is a self-contained unit that grew past
 * what inline code could hold without pushing dxfBridge.ts over the
 * 2000-line guardrail (CLAUDE.md: ">2000 не допускать").
 *
 * Public surface:
 *   - buildImportedSpaces(document, convertEntity, options) : DrawingSpace[]
 *
 * Why routing is not trivial (and why the caller can't just iterate
 * `getSupportedEntities(doc)`): AutoCAD stores paper-space entities in
 * THREE different places depending on the DXF authoring tool and the
 * layout's provenance:
 *
 *   A) Top-level ENTITIES section, tagged `paperSpace=1` (group 67) and
 *      `layoutName` (group 410) — the "legacy" format that used to work
 *      before multi-layout support was added to DXF. This is what
 *      `getSupportedEntities(doc)` returns.
 *
 *   B) Top-level ENTITIES section, tagged `paperSpace=1` but WITHOUT a
 *      `layoutName` — these belong to the DEFAULT paper space
 *      (`*Paper_Space` BLOCK_RECORD), which in turn links to the layout
 *      whose `ownerHandle` matches the `*Paper_Space` BLOCK_RECORD
 *      handle. AutoCAD 2018+ emits these when a drawing only has one
 *      paper-space tab and the user hasn't renamed it.
 *
 *   C) Inside `*Paper_Space<N>` BLOCK records (BLOCKS section) — the
 *      canonical place for entities in non-default layouts. Every
 *      LAYOUT object in the OBJECTS section owns one such BLOCK_RECORD
 *      via a handle pair: LAYOUT.330 → BLOCK_RECORD.5 and
 *      BLOCK_RECORD.340 → LAYOUT.5. The BLOCK itself (in BLOCKS
 *      section) is keyed by the same *Paper_Space<N> name.
 *
 * Before this fix the importer only handled (A). (B) fell back to a
 * hardcoded "Layout1" name. (C) was silently dropped — the paper-space
 * BLOCK records ended up in `document.blocks` but never routed into
 * `spaces[]`. Real-world Russian-Autocad files (e.g. АБИ1.dxf from the
 * reported bug) pinned the entire bug on (C) because each of the four
 * layouts was authored by AutoCAD 2018 in exactly the canonical way.
 */
import {
  getBlocksSection,
  getObjectsSection,
  getSupportedEntities,
  getSupportedEntitiesDraining,
  getTablesSection,
  type DxfBlockDefinition,
  type DxfDocument,
  type DxfSupportedEntity,
} from "@dxf-ai-editor/dxf-io";

import { buildDefaultLayout, convertRawLayout } from "./dxfBridge.layouts";
import type { AttdefEntity, DrawingEntity, DrawingSpace, Layout } from "./model";
import { collectXdataAuthoritativeVpHandles } from "./viewportFrozenLayersImport";

/**
 * PR1b Codex P2 FIX-2 — `onEntityImported` returns `DrawingEntity | void`. A
 * HEALING hook returns a real (healed) entity that the routing layer must write
 * back into the retained shell; a SIDE-EFFECT-only hook (a concise callback such
 * as `(e) => seen.push(e)`, which returns the new array LENGTH — a number) must
 * NOT have its return value treated as the replacement entity. This guard lets
 * the routing layer use the hook's return value ONLY when it is a genuine
 * DrawingEntity (a non-null object with a string `type` discriminant — how every
 * DrawingEntity variant is shaped). A number / string / array / null otherwise
 * falls back to the `converted` entity, restoring backward compatibility for
 * side-effect-only hooks while preserving the heal write-back.
 */
function isDrawingEntity(value: DrawingEntity | void): value is DrawingEntity {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as { type?: unknown }).type === "string"
  );
}

// NOTE: `buildDefaultLayout` (default A4 page setup with SYNCED flat plot
// mirrors) moved to dxfBridge.layouts.ts so the LAYOUT NEW / RENAME
// materialisation path in the web reducer shares the importer's single
// source of truth (Codex P2-2 PR #311).

/**
 * Default tab name for the SYNTHETIC fallback paper-space layout — the
 * space `resolveTopLevelEntityDraft` routes top-level paper entities
 * (group 67=1) WITHOUT a layoutName (group 410) into when no LAYOUT in
 * the document claims the un-suffixed `*Paper_Space` BLOCK_RECORD
 * (legacy DXFs with no OBJECTS LAYOUT entries). Exported as the single
 * source of truth shared by the importer (`fallbackLayoutName` default)
 * and the LAYOUT DELETE reducer (`geometryReducerLayout.ts`), which must
 * mirror this routing to purge 67=1-no-410 raw passthrough records when
 * that ownerless fallback space is removed (Codex P2-1 r5, PR #311).
 */
export const DEFAULT_PAPER_FALLBACK_LAYOUT_NAME = "Layout1";

export type BuildImportedSpacesOptions = {
  /**
   * Fallback tab name when a paper-space entity carries no layoutName
   * AND no LAYOUT object matches the default *Paper_Space block record.
   * Retained for backwards compatibility with legacy DXFs that have
   * neither an OBJECTS section nor a populated `layoutName` group.
   */
  fallbackLayoutName: string;
  /**
   * Phase 4 Wave 2 Task 3 — streaming hook.
   *
   * When set, invoked synchronously for every DrawingEntity that lands
   * in a space draft, at the moment it is appended. Callers can use this
   * to stream entities into a column-store cache (e.g. the editorStore's
   * compact cache) so the peak intermediate cost — the entity array
   * holding ~150 MB on 685k-entity drawings — never has to live both in
   * the draft AND in the cache simultaneously.
   *
   * Default behaviour is unchanged when the option is omitted: entities
   * still land in `space.entities[]` as a plain array.
   *
   * The default behaviour of the importer is to ALSO keep the entity in
   * `space.entities` — this hook is additive, not replacing. The
   * downstream consumer (editorStore) is responsible for deciding
   * whether to retain the array reference after the import settles.
   *
   * PR1b Codex P2 (streaming heal write-back) — the streaming worker's hook
   * HEALS the entity's dangling refs before buffering it for the chunk
   * pipeline. It returns the HEALED entity so the routing layer can write it
   * back into the RETAINED shell entity (Viewports kept in `draft.entities[]`
   * under `discardEntitiesAfterStreaming`). Without the write-back the retained
   * Viewport — used for `viewportsBySpaceId` / the export-audit baseline — would
   * keep the ORIGINAL dirty refs while only the discarded chunk clone is healed.
   * Returning `void`/`undefined` (the legacy / non-healing hook) leaves the
   * converted entity unchanged, preserving backward compatibility.
   */
  onEntityImported?: (
    entity: DrawingEntity,
    spaceId: string,
  ) => DrawingEntity | void;
  /**
   * Phase 4 Wave 2 Task 4 — streaming-only mode.
   *
   * When `true` AND `onEntityImported` is set, the routing layer
   * forwards every converted entity to the hook but does NOT push
   * them into `draft.entities[]`. The returned `DrawingSpace[]` shells
   * carry empty `entities` arrays.
   *
   * This is the memory-saving mode used by the worker streaming path
   * in `apps/web/src/app/workers/dxf-parser.worker.ts` — the worker
   * forwards every chunk to the main thread via `entity-chunk`
   * messages, the main thread appends them to the compact cache, and
   * the final `result` message contains a document SHELL with empty
   * per-space entity arrays. Skipping the in-memory `entities[]` cuts
   * the worker peak memory roughly in half on 685k-entity drawings,
   * because the hook can drain into a buffer and flush incrementally
   * rather than holding every converted entity until the bridge call
   * returns.
   *
   * Has no effect when `onEntityImported` is undefined — without a
   * sink for the entities, dropping them would silently lose the import.
   *
   * Default: `false`.
   */
  discardEntitiesAfterStreaming?: boolean;
  /**
   * Codex P5 / Option B follow-up (2026-05-18) — drain top-level
   * `document.sections[entities].entities` as bridge consumes them, so
   * V8 can reclaim entity payloads while the bridge emits DrawingEntity
   * chunks downstream. Only safe in single-pass consumers (worker)
   * where the document is not re-read after bridge completes.
   *
   * When `true`, the routing layer iterates via the destructive
   * `getSupportedEntitiesDraining` generator instead of the
   * filter-based `getSupportedEntities`. Default: `false`.
   */
  drainTopLevelEntities?: boolean;
};

/**
 * Callback that turns a raw DXF supported entity into a DrawingEntity
 * or AttdefEntity. Extracted as a parameter so the routing logic does
 * not re-import the entire entity-conversion layer from dxfBridge.ts
 * (which would create a circular module dependency).
 */
export type ConvertEntityFn = (
  entity: DxfSupportedEntity,
  entityId: string,
  spaceId: string,
) => DrawingEntity | AttdefEntity;

type SpaceDraft = {
  id: string;
  kind: DrawingSpace["kind"];
  name: string;
  entities: DrawingEntity[];
  tabOrder: number;
  // Link back to the owning BLOCK_RECORD handle so the BLOCK routing
  // pass can find the right space draft quickly. May be empty for
  // layouts that had no OBJECTS entry (synthetic fallback).
  blockRecordHandle: string;
};

/**
 * Build the final DrawingSpace[] for an imported DXF, correctly routing
 * entities from every source (ENTITIES section, paper-space BLOCK
 * records) to the LAYOUT-object-derived tab name.
 */
export function buildImportedSpaces(
  document: DxfDocument,
  convertEntity: ConvertEntityFn,
  options: BuildImportedSpacesOptions,
) {
  const objectsSection = getObjectsSection(document);
  const tablesSection = getTablesSection(document);
  const blocksSection = getBlocksSection(document);

  // EXPORT-VP-FROZEN — capture WHICH viewports have XDATA that AUTHORITATIVELY
  // owns the frozen-layer set (carries frozen-layer 1000 groups) BEFORE this
  // function drains the top-level ENTITIES array under `drainTopLevelEntities`.
  // A post-drain scan misses every top-level paper-space VIEWPORT on the
  // streaming worker path, so its authority would be lost and stale native 331
  // handles merged back into `frozenLayers` (@codex #486 P2). Returned to the
  // bridge and threaded into `resolveImportedVpFrozen`.
  const xdataAuthoritativeVpHandles =
    collectXdataAuthoritativeVpHandles(document);

  // Entity ids must be globally unique across ALL spaces (model +
  // layouts) because the DrawingDocument schema enforces it. One shared
  // counter avoids collisions when the same fallback generator is
  // invoked across multiple routing passes.
  let nextEntityIndex = 1;
  const nextId = (type: DxfSupportedEntity["type"]) =>
    `${type.toLowerCase()}-${nextEntityIndex++}`;

  // ── Index block records by handle → name and by name → record.
  // TABLES BLOCK_RECORD is the single source of truth for the
  // BLOCK_RECORD handle (the BLOCKS section stores a different
  // `block.handle` that points at the BLOCK itself, not the record).
  const blockRecordNameByHandle = new Map<string, string>();
  if (tablesSection) {
    for (const rec of tablesSection.blockRecords) {
      if (rec.handle && rec.name) {
        blockRecordNameByHandle.set(rec.handle, rec.name);
      }
    }
  }

  // ── Index blocks by name for fast lookup during routing.
  const blockByName = new Map<string, DxfBlockDefinition>();
  if (blocksSection) {
    for (const block of blocksSection.blocks) {
      if (block.name) blockByName.set(block.name, block);
    }
  }

  // ── Seed space drafts from the OBJECTS-section LAYOUT list.
  // AutoCAD emits one LAYOUT per tab; "Model" is always present even
  // when the drawing has no paper-space tabs. This seeding makes the
  // routing robust even when the LAYOUT objects carry no entities (so
  // the tab still shows up as an empty space).
  const drafts: SpaceDraft[] = [];
  const draftById = new Map<string, SpaceDraft>();
  const draftByBlockRecordHandle = new Map<string, SpaceDraft>();
  const draftByBlockName = new Map<string, SpaceDraft>();
  // @codex — identity set of every draft this bridge INVENTED (no LAYOUT
  // object behind it). Used below to make sure only an invented fallback can
  // claim the unowned default *Paper_Space block.
  const syntheticDrafts = new Set<SpaceDraft>();
  // Disambiguation: the raw `name` field on a LAYOUT is a user-editable
  // string and is not guaranteed to be unique across layouts (the DXF
  // format has no constraint preventing two tabs with the same name
  // from co-existing, although AutoCAD itself rejects them in the UI).
  // When we see a collision we rename subsequent drafts "<name> (1)",
  // "<name> (2)", ... — mirror AutoCAD's tab-rename suffix semantics.
  const seenNames = new Set<string>();
  const disambiguate = (name: string): string => {
    if (!seenNames.has(name)) {
      seenNames.add(name);
      return name;
    }
    for (let i = 1; i < 1000; i++) {
      const candidate = `${name} (${i})`;
      if (!seenNames.has(candidate)) {
        seenNames.add(candidate);
        return candidate;
      }
    }
    // Pathological: 1000 drafts with the same name. Fall back to a
    // handle-suffixed name so we never lose layouts silently.
    const fallback = `${name} (${Date.now()})`;
    seenNames.add(fallback);
    return fallback;
  };

  // Maps spaceId → Layout model object. Keyed by the disambiguated
  // spaceId (e.g. "layout:Beta (1)") so Scene3DStage can look up the
  // layout setup by stable id without stripping and re-matching names
  // (which fails on duplicate-name drawings — Codex P1 #162).
  const layoutsBySpaceId: Record<string, Layout> = {};

  if (objectsSection) {
    // Sort by tabOrder so the resulting spaces[] reflects AutoCAD's
    // tab bar ordering. Model always sorts first regardless of tab
    // order because kind="model" must come before kind="layout".
    const orderedLayouts = [...objectsSection.layouts].sort(
      (a, b) => (a.tabOrder ?? 0) - (b.tabOrder ?? 0),
    );
    for (const layout of orderedLayouts) {
      const rawName = layout.name?.trim();
      if (!rawName) continue;
      const isModel = rawName === "Model";
      const uniqueName = disambiguate(rawName);
      const spaceId = isModel ? "model" : `layout:${uniqueName}`;
      const draft: SpaceDraft = {
        id: spaceId,
        kind: isModel ? "model" : "layout",
        name: isModel ? "Model" : uniqueName,
        entities: [],
        tabOrder: layout.tabOrder ?? 0,
        blockRecordHandle: layout.ownerHandle ?? "",
      };
      drafts.push(draft);
      draftById.set(draft.id, draft);
      if (draft.blockRecordHandle) {
        draftByBlockRecordHandle.set(draft.blockRecordHandle, draft);
      }
      const blockName = blockRecordNameByHandle.get(draft.blockRecordHandle);
      if (blockName) {
        draftByBlockName.set(blockName, draft);
      }
      // Track spaceId → converted Layout for stable lookup by id.
      // Model space has no LAYOUT page-setup object so skip it.
      if (!isModel) {
        layoutsBySpaceId[spaceId] = convertRawLayout(layout);
      }
    }
  }

  // ── Ensure a Model space always exists. Legacy DXFs that omit the
  // OBJECTS section entirely need the bridge to synthesise a Model
  // space so top-level ENTITIES have somewhere to land.
  let modelDraft = drafts.find((d) => d.kind === "model");
  if (!modelDraft) {
    modelDraft = {
      id: "model",
      kind: "model",
      name: "Model",
      entities: [],
      tabOrder: 0,
      blockRecordHandle: "",
    };
    drafts.unshift(modelDraft);
    draftById.set(modelDraft.id, modelDraft);
    // Seed the model block-record link too so *Model_Space block
    // entities (if present) route to the Model space.
    const modelBlockRecordHandle = findBlockRecordHandleByName(
      blockRecordNameByHandle,
      "*Model_Space",
    );
    if (modelBlockRecordHandle) {
      modelDraft.blockRecordHandle = modelBlockRecordHandle;
      draftByBlockRecordHandle.set(modelBlockRecordHandle, modelDraft);
    }
    draftByBlockName.set("*Model_Space", modelDraft);
  } else if (!draftByBlockName.has("*Model_Space")) {
    // Model was present in OBJECTS — still ensure *Model_Space block
    // entities route to it even if the BLOCK_RECORD / LAYOUT handle
    // pair is broken in the source file.
    draftByBlockName.set("*Model_Space", modelDraft);
  }

  // ── Default paper space fallback: *Paper_Space (no numeric suffix)
  // belongs to whichever LAYOUT has it as owner. Cache this draft so
  // top-level ENTITIES-section paper entities without a layoutName can
  // still find a home.
  const defaultPaperSpaceDraft = draftByBlockName.get("*Paper_Space");

  // Phase 4 Wave 2 Task 4 — streaming-only mode is gated on BOTH
  // `discardEntitiesAfterStreaming` AND a real `onEntityImported`
  // sink. Without the hook the entities would be dropped silently,
  // which would silently corrupt every legacy caller — so we require
  // the explicit opt-in pair.
  const skipPushToDraft =
    options.discardEntitiesAfterStreaming === true &&
    options.onEntityImported !== undefined;

  // ── Pass 1: route top-level ENTITIES section.
  // Codex P5 / Option B (2026-05-18) — when `drainTopLevelEntities` is
  // set, walk via the destructive generator so the
  // `document.sections[entities].entities` array's slots are nulled as
  // each entity is consumed (entity payloads become GC-eligible while
  // the rest of the bridge runs). The non-draining filter variant
  // returns a fresh array of references (~150 MB transient on P35R 685k)
  // and keeps the original array pinned until the caller releases the
  // whole DxfDocument.
  const topLevelEntities = options.drainTopLevelEntities === true
    ? getSupportedEntitiesDraining(document)
    : getSupportedEntities(document);
  for (const entity of topLevelEntities) {
    if (entity.type === "ATTDEF" || entity.type === "SEQEND") continue;
    const draft = resolveTopLevelEntityDraft(
      entity,
      {
        modelDraft,
        defaultPaperSpaceDraft,
        drafts,
        draftById,
        fallbackLayoutName: options.fallbackLayoutName,
        syntheticDrafts,
      },
    );
    if (!draft) continue;
    const entityId =
      entity.common.handle?.trim() || nextId(entity.type);
    const converted = convertEntity(entity, entityId, draft.id);
    if (converted.type === "Attdef") continue;
    // Phase 4 Wave 2 Task 3 — streaming hook (additive, no-op when the caller
    // did not opt in). PR1b Codex P2 — run it BEFORE the retain-push so a
    // healing hook's returned (healed) entity is what lands in the RETAINED
    // shell. PR1b Codex P2 FIX-2 — use the return value as the replacement ONLY
    // when it is a genuine DrawingEntity; a side-effect-only hook returning a
    // number/void leaves `converted` in place (see `isDrawingEntity`).
    const hookResult = options.onEntityImported?.(converted, draft.id);
    const healed = isDrawingEntity(hookResult) ? hookResult : converted;
    // MC3 fix-6 — Viewport entities are retained in draft.entities[] even
    // when streaming discards all other entities (skipPushToDraft=true), so
    // viewportsBySpaceId survives entity discard and scene rendering works.
    // PR1b Codex P2 — push the HEALED entity (not the dirty `converted`) so the
    // retained shell Viewport has no dangling ref in the export-audit baseline.
    if (!skipPushToDraft || healed.type === "Viewport") {
      draft.entities.push(healed);
    }
  }

  // ── Guarantee at least one paper-space layout survives the import.
  //
  // AutoCAD and `ezdxf` both hold this floor at ALL times, not just on
  // the WRITE side — this codebase's OWN `spaceMutationGuards.ts` already
  // enforces the AutoCAD-parity half of it for LAYOUT DELETE ("the LAST
  // remaining paper-space layout cannot be deleted... ezdxf: 'Can not
  // delete last layout.'"). `ezdxf` enforces the SAME floor on the READ
  // side too: `ezdxf.layouts.layouts.Layouts.load()` —
  // `if len(layouts) < 2: layouts.restore("Model", MODEL_SPACE_R2000,
  // taborder=0); layouts.restore("Layout1", PAPER_SPACE_R2000,
  // taborder=1)` — synthesising a "Layout1" paper space whenever the
  // OBJECTS section produced fewer than 2 layouts. AutoCAD mirrors this:
  // opening a legacy/hand-authored DXF with no LAYOUT records
  // materialises a "Layout1" tab.
  //
  // Pass 1 above only synthesises a layout draft when paper-space FLAGGED
  // content actually exists (a group-67=1 entity) — a DXF with zero LAYOUT
  // objects (a converter export, an R12-derived file, or any DXF authored
  // without ever touching a paper layout) reaches this point with `drafts`
  // holding ONLY the Model space. Without this floor the import silently
  // deletes paper space from every such file (the tab strip collapses to
  // Model alone with no way to get the layout back).
  //
  // POSITION IS LOAD-BEARING (@codex): this runs AFTER Pass 1 so it cannot
  // double-create a tab Pass 1 already made, but BEFORE Pass 2 so the
  // synthesised draft is registered in `draftByBlockName` and the default
  // `*Paper_Space` BLOCK's entities ROUTE INTO IT. Synthesising after Pass 2
  // instead produced a visible but EMPTY "Layout1" while the paper-space
  // content that block carried stayed dropped — a tab that claimed to
  // recover paper space without its contents.
  if (!drafts.some((draft) => draft.kind === "layout")) {
    const fallbackDraft = createSyntheticLayoutDraft(
      drafts,
      draftById,
      options.fallbackLayoutName,
      syntheticDrafts,
    );
    // This IS the drawing's only layout (not an "unexpected layoutName"
    // synthetic draft sorted to the end via MAX_SAFE_INTEGER) — give it
    // taborder=1 immediately after Model, mirroring ezdxf's
    // `restore("Layout1", ..., taborder=1)` exactly so the exported
    // LAYOUT's group-71 taborder stays a small, sane integer.
    fallbackDraft.tabOrder = 1;
  }

  // Let the FALLBACK layout claim the default `*Paper_Space` BLOCK whenever no
  // REAL layout has claimed it (@codex). This deliberately sits OUTSIDE the
  // guard above: in a MIXED zero-LAYOUT file — a top-level 67=1 paper entity
  // AND entities inside the `*Paper_Space` block — Pass 1 has already created
  // the fallback draft from the top-level entity, so the guard is false, yet
  // `resolveTopLevelEntityDraft` only registers the draft in `draftById`.
  // Without this, Pass 2 still finds no `draftByBlockName` entry for
  // `*Paper_Space`, skips it, and silently drops that block's entities.
  //
  // Only the un-suffixed `*Paper_Space` is claimed: it is THE default paper
  // space by DXF convention, so its content belongs to the drawing's fallback
  // layout. A `*Paper_Space<N>` block with no LAYOUT object is malformed and is
  // left alone rather than merged into one tab, which would silently
  // concatenate what were authored as separate layouts. A real LAYOUT that
  // owns `*Paper_Space` already populated `draftByBlockName`, so the `has`
  // check keeps this strictly additive.
  if (!draftByBlockName.has("*Paper_Space")) {
    const fallbackDraft = draftById.get(`layout:${options.fallbackLayoutName}`);
    // SYNTHETIC ONLY (@codex). Matching on the id alone would also match a
    // REAL authored tab that merely happens to be named "Layout1" — a very
    // common name — whose LAYOUT owns a different (or broken) BLOCK_RECORD.
    // Appending the unclaimed block's entities to that tab would silently
    // change authored layout membership. The identity set is the honest test;
    // an empty `blockRecordHandle` is not, because a real LAYOUT with a
    // missing ownerHandle also has an empty one.
    if (fallbackDraft && syntheticDrafts.has(fallbackDraft)) {
      draftByBlockName.set("*Paper_Space", fallbackDraft);
    }
  }

  // ── Pass 2: route paper-space BLOCK records. Every LAYOUT's tab
  // content lives in the *Paper_Space<N> block whose BLOCK_RECORD
  // handle matches layout.ownerHandle. Skip *Model_Space because
  // its entities are already routed through Pass 1 when the file
  // uses the legacy format; for modern files that put model-space
  // entities inside the *Model_Space block, we route them too.
  if (blocksSection) {
    for (const block of blocksSection.blocks) {
      if (!block.name) continue;
      const isPaperSpaceBlock =
        block.name === "*Paper_Space" ||
        /^\*Paper_Space\d+$/.test(block.name);
      const isModelSpaceBlock = block.name === "*Model_Space";
      if (!isPaperSpaceBlock && !isModelSpaceBlock) continue;
      const draft = draftByBlockName.get(block.name);
      if (!draft) continue;
      for (const rawEntity of block.entities) {
        if (rawEntity.kind !== "supported-entity") continue;
        if (
          rawEntity.type === "ATTDEF" ||
          rawEntity.type === "SEQEND" ||
          rawEntity.type === "ATTRIB"
        ) {
          continue;
        }
        const entityId =
          rawEntity.common.handle?.trim() || nextId(rawEntity.type);
        const converted = convertEntity(rawEntity, entityId, draft.id);
        if (converted.type === "Attdef") continue;
        // Phase 4 Wave 2 Task 3 — streaming hook (run before the retain-push;
        // see Pass 1). PR1b Codex P2 — write the healed entity back into the
        // retained shell so the export-audit baseline carries no dangling ref.
        // PR1b Codex P2 FIX-2 — only a genuine DrawingEntity return replaces
        // `converted`; a side-effect-only hook return (number/void) is ignored.
        const hookResult = options.onEntityImported?.(converted, draft.id);
        const healed = isDrawingEntity(hookResult) ? hookResult : converted;
        // MC3 fix-6 — same Viewport-retain logic as Pass 1.
        if (!skipPushToDraft || healed.type === "Viewport") {
          draft.entities.push(healed);
        }
      }
    }
  }

  // KNOWN GAP 1 — INSERT (deliberately out of scope here, raised by local
  // Codex review and documented in this PR's Verification report). The floor
  // runs for EVERY import, including the one whose result is merged INTO a
  // live drawing (the INSERT half of `importDxfText`, where
  // `mergeImportedGeometryDocument` unions imported spaces by id). A host that
  // already has its own tabs therefore gains a stray empty "Layout1" that
  // exists in no file. It is provably non-destructive — the synthetic space
  // contributes NO `layouts[name]` record, so it cannot overwrite a host
  // layout record (pinned by `importFallbackLayoutMerge.test.ts`) — and it is
  // not reachable from the shipping UI, whose file input always takes the
  // OPEN/replace path. The correct fix is an OPEN-vs-INSERT gate, but it has
  // to be applied at a choke point BOTH the sync and the worker-backed async
  // import funnel through; gating only the sync caller would leave the two
  // paths behaving differently, which is worse than the current consistent
  // behaviour. Deferred rather than half-applied.
  //
  // KNOWN GAP 2 — EXPORT. The floor above gives
  // the document a paper SPACE and — via `buildDefaultLayout` below — a
  // `layoutsBySpaceId` record, but NOT an entry in `document.layouts`, which
  // `dxfBridge.import.ts` builds solely from `importLayouts(getObjectsSection
  // (...))`. Since `exportLayouts(document.layouts)` is what serialises
  // OBJECTS/LAYOUT, re-exporting such a drawing still writes ZERO LAYOUT
  // records: the tab survives a reopen only because THIS floor re-synthesises
  // it, not because the file carries it. `ezdxf`/AutoCAD would persist the
  // restored layout. This is NOT a regression — the pre-fix export emitted
  // zero LAYOUT records for these files too (verified both ways) — so it is
  // an unimproved gap, not one introduced here. Closing it means minting a
  // *Paper_Space BLOCK_RECORD + owner handle for every synthetic draft, which
  // reaches into the handle/skeleton machinery and needs its own conformance
  // run against the ezdxf audit gates.

  // ── Finalise the space order: Model first, then layouts by tabOrder.
  drafts.sort((left, right) => {
    if (left.kind === "model" && right.kind !== "model") return -1;
    if (left.kind !== "model" && right.kind === "model") return 1;
    return left.tabOrder - right.tabOrder;
  });

  // PDF-6.1: seed default plot settings for synthetic / from-scratch layout
  // spaces. A layout draft created without a DXF LAYOUT object (legacy DXFs
  // with paper-space entities in ENTITIES but no OBJECTS LAYOUT, or a runtime
  // LAYOUT NEW) has no `layoutsBySpaceId` entry yet — give it the canonical
  // A4 page so the exporter reads a valid configuration from the model.
  for (const draft of drafts) {
    if (draft.kind !== "layout") continue;
    if (layoutsBySpaceId[draft.id]) continue;
    const tabOrder = Number.isFinite(draft.tabOrder) ? draft.tabOrder : 0;
    layoutsBySpaceId[draft.id] = buildDefaultLayout(draft.name, tabOrder);
  }

  // NOTE: we do not annotate the return tuple as `DrawingSpace` here
  // because zod's inferred output type requires every nested `.default`
  // field (including `refeditWorkingSet`) to be present, while in
  // practice dxfBridge.ts hands these drafts through to the DrawingDocument
  // schema which re-applies defaults on superRefine. The existing
  // helpers (helpers.ts, dxfBridge.ts itself before this split) rely on
  // the same pattern — see the baseline typecheck errors in model.test.ts
  // and helpers.ts for the identical gap. Fixing this gap is out of
  // scope for the multi-layout import fix.
  const spaces = drafts.map(({ id, kind, name, entities }) => ({
    id,
    kind,
    name,
    entities,
  }));
  return { spaces, layoutsBySpaceId, xdataAuthoritativeVpHandles };
}

function findBlockRecordHandleByName(
  blockRecordNameByHandle: Map<string, string>,
  name: string,
): string | undefined {
  for (const [handle, recName] of blockRecordNameByHandle) {
    if (recName === name) return handle;
  }
  return undefined;
}

function resolveTopLevelEntityDraft(
  entity: DxfSupportedEntity,
  ctx: {
    modelDraft: SpaceDraft;
    defaultPaperSpaceDraft: SpaceDraft | undefined;
    drafts: SpaceDraft[];
    draftById: Map<string, SpaceDraft>;
    fallbackLayoutName: string;
    syntheticDrafts: Set<SpaceDraft>;
  },
): SpaceDraft | null {
  const layoutName = entity.common.layoutName?.trim();
  const isPaper = entity.common.space === "paper" || Boolean(layoutName);
  if (!isPaper) {
    return ctx.modelDraft;
  }
  // Paper-space entity with an explicit layoutName (group 410) — route
  // to the OBJECTS-derived draft if we have one, otherwise synthesise
  // a fresh layout space so the content is never lost.
  if (layoutName) {
    const byName = findDraftByLayoutName(ctx.draftById, layoutName);
    if (byName) return byName;
    // Synthetic layout space for a layoutName we did not see in the
    // OBJECTS section. This happens with legacy DXFs that carry the
    // paper-space entities in ENTITIES but no LAYOUT objects.
    return createSyntheticLayoutDraft(
      ctx.drafts,
      ctx.draftById,
      layoutName,
      ctx.syntheticDrafts,
    );
  }
  // Paper-space entity without a layoutName → default *Paper_Space.
  if (ctx.defaultPaperSpaceDraft) return ctx.defaultPaperSpaceDraft;
  // No OBJECTS section → fall back to the legacy synthetic layout.
  const existing = ctx.draftById.get(`layout:${ctx.fallbackLayoutName}`);
  if (existing) return existing;
  return createSyntheticLayoutDraft(
    ctx.drafts,
    ctx.draftById,
    ctx.fallbackLayoutName,
    ctx.syntheticDrafts,
  );
}

function createSyntheticLayoutDraft(
  drafts: SpaceDraft[],
  draftById: Map<string, SpaceDraft>,
  name: string,
  // @codex — every synthetic (LAYOUT-less) draft is recorded here so the
  // *Paper_Space claim below can tell an INVENTED tab from a real authored
  // one that merely happens to be named "Layout1".
  syntheticDrafts: Set<SpaceDraft>,
): SpaceDraft {
  const id = `layout:${name}`;
  const draft: SpaceDraft = {
    id,
    kind: "layout",
    name,
    entities: [],
    // Synthetic drafts sort to the end of the tab bar because we have
    // no authoritative tabOrder for them (the LAYOUT object is
    // missing). MAX_SAFE_INTEGER keeps them stable while guaranteeing
    // they never collide with a real tabOrder.
    tabOrder: Number.MAX_SAFE_INTEGER,
    blockRecordHandle: "",
  };
  drafts.push(draft);
  draftById.set(id, draft);
  syntheticDrafts.add(draft);
  return draft;
}

function findDraftByLayoutName(
  draftById: Map<string, SpaceDraft>,
  name: string,
): SpaceDraft | undefined {
  // Fast path: canonical id matches the layout name verbatim.
  const direct = draftById.get(`layout:${name}`);
  if (direct) return direct;
  // Fall back to a scan because disambiguation (e.g. "А3_г (1)") can
  // change the id while the stored `name` still matches the original
  // bytes of the layoutName group.
  for (const draft of draftById.values()) {
    if (draft.kind === "layout" && draft.name === name) return draft;
  }
  return undefined;
}
